import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Gift, Heart, GraduationCap, Send, Sparkles, ShieldCheck, Check, UserCircle2 } from "lucide-react";

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden pt-6 pb-10 md:py-28">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-background to-sky/10" />
      <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-accent/5 blur-3xl animate-pulse-soft will-change-transform" />
      <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-secondary/10 blur-3xl animate-pulse-soft will-change-transform" style={{ animationDelay: "1.5s" }} />

      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-10 items-center">
          <div className="animate-hero-fade-up">
            
            <div className="inline-flex items-center gap-2 bg-accent/15 text-accent-strong rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold mb-4 md:mb-6">
              <Sparkles className="h-4 w-4" />
              Set it once. Speshly remembers forever.
            </div>

            <h1 className="font-heading text-primary mb-3 md:mb-6">
              Never miss a moment that{" "}
              <span className="font-script text-gradient-accent text-[1.15em] leading-none pr-1">matters</span>
            </h1>
            <p className="text-base md:text-xl text-muted-foreground mb-5 md:mb-8 max-w-lg font-body leading-relaxed">
              Never let another birthday or anniversary slip by. Save the moments that matter once, and Speshly will write the card in your words and deliver it on the day — every year, automatically.
            </p>
            <div className="flex flex-wrap gap-3 md:gap-4">
              <Link to="/signup">
                <Button variant="hero" size="lg" className="text-base px-7 md:px-8 py-5 md:py-6">
                  Start for free
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button variant="hero-outline" size="lg" className="text-base px-7 md:px-8 py-5 md:py-6">
                  See how it works
                </Button>
              </a>
            </div>

            {/* Trust row */}
            <div className="mt-5 md:mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-success" /> 5 starter credits to try</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-accent-strong" /> 7-day Premium trial — no card</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-success" /> 2-minute setup</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-success" /> Cancel anytime</span>
            </div>

            {/* Founder's note — transparent, no fake reviews */}
            <figure className="mt-5 max-w-md rounded-xl border border-border bg-background/60 backdrop-blur-sm p-4">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-accent-strong mb-2">
                <UserCircle2 className="h-3.5 w-3.5" />
                From the founder
              </div>
              <blockquote className="text-sm text-foreground font-body leading-relaxed">
                "The people you love shouldn't have to wonder if you were thinking of them. That's why Speshly exists."
              </blockquote>
              <figcaption className="mt-2 text-xs text-muted-foreground">
                — Speshly founder · Built in Australia · Open beta
              </figcaption>
            </figure>
          </div>

          {/* Card mockup */}
          <div className="relative flex justify-center lg:justify-end animate-hero-scale-in mt-2 lg:mt-0 lg:pr-6">
            {/* Main card mockup */}
            <div className="relative w-60 sm:w-72 lg:w-[420px]">
              {/* The greeting card */}
              <div className="rounded-2xl overflow-hidden shadow-elevation-4 shadow-accent-glow border border-border bg-gradient-to-br from-accent/10 via-secondary/20 to-sky/10">
                <div className="p-6 sm:p-8 text-center">
                  <div className="text-5xl sm:text-6xl mb-4">🎂</div>
                  <p className="font-script text-3xl sm:text-4xl text-primary mb-2">Happy Birthday!</p>
                  <p className="font-script text-lg sm:text-xl text-foreground/80 leading-snug mb-4">
                    Wishing you the happiest of birthdays, filled with love, laughter, and all the things that make your heart sing.
                  </p>
                  <div className="flex items-center justify-center gap-1.5 text-xs text-accent-strong font-semibold">
                    <Sparkles className="h-3.5 w-3.5" />
                    Generated by Speshly AI
                  </div>
                </div>
                {/* Bottom bar */}
                <div className="bg-primary/5 border-t border-border px-6 py-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">To: Mom</span>
                  <div className="flex items-center gap-1.5 text-xs text-accent font-medium">
                    <Send className="h-3 w-3" />
                    Auto-send Apr 12
                  </div>
                </div>
              </div>

              {/* Floating notification cards */}
              <div className="absolute -left-4 sm:-left-8 lg:-left-10 top-20 lg:top-24 animate-float animate-hero-slide-left">
                <div className="flex items-center gap-2 rounded-xl border border-accent/20 px-3 sm:px-4 py-2 sm:py-2.5 shadow-elevation-3 bg-background/95 backdrop-blur-sm">
                  <Gift className="h-5 w-5 text-accent" />
                  <div>
                    <p className="font-heading font-semibold text-xs sm:text-sm text-primary">Birthday</p>
                    <p className="text-[10px] text-muted-foreground">Card sent ✓</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-3 sm:-right-6 lg:-right-4 top-1/2 animate-float-delayed animate-hero-slide-right">
                <div className="flex items-center gap-2 rounded-xl border border-secondary px-3 sm:px-4 py-2 sm:py-2.5 shadow-elevation-3 bg-background/95 backdrop-blur-sm">
                  <Heart className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-heading font-semibold text-xs sm:text-sm text-primary">Anniversary</p>
                    <p className="text-[10px] text-muted-foreground">In 5 days</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 animate-float animate-hero-slide-up">
                <div className="flex items-center gap-2 rounded-xl border border-sky/40 px-3 sm:px-4 py-2 sm:py-2.5 shadow-elevation-3 bg-background/95 backdrop-blur-sm">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-heading font-semibold text-xs sm:text-sm text-primary">Graduation</p>
                    <p className="text-[10px] text-muted-foreground">Auto-send on</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
